import React, { useCallback, useEffect, useMemo } from "react";
import { SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";

import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import {  Clock, Info, LoaderCircle, Minus, Plus, X } from "lucide-react";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addToCart } from "@/store/slices/appSlice";
import { useCombineProducts, useCustomPaginatedProducts } from "@/hooks";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { listUserInfos } from "@/graphql/queries";

import { ScrollArea } from "../ui/scroll-area";
const OtherSellersSheet = React.memo(
  ({
    className,
    mainProductDetails,
    currentVariant,
    setOtherSellers,
    setSheetOpen,
    isMainProductloading,
  }: {
    className?: string;
    mainProductDetails?: any;
    currentVariant: any;
    setOtherSellers: (sellers: any[]) => void;
    isMainProductloading?: boolean;
    setSheetOpen: (open: boolean) => void;
  }) => {
    const { brands, categories } = useAppSelector((state) => state.app);
    const [otherProducts, setOtherProducts] = React.useState<any[]>([]);
    const [quantity, setQuantity] = React.useState(1);
    const [productsLoading, setProductsLoading] = React.useState(false);
    const dispatch = useAppDispatch();
    const [sellerNames, setSellerNames] = React.useState<{ [key: string]: string }>({});
    const [fetchFlags, setFetchFlags] = React.useState({
      products: false,
      sellers: false,
    });
    const variables = useMemo(() => {
      if (!mainProductDetails) return {};

      return {
        filter: {
          name: { eq: mainProductDetails?.name },
          brand_id: { eq: mainProductDetails?.brand_id },
          seller_id: { ne: mainProductDetails?.seller_id },
          is_active: { eq: true },
          is_deleted: { eq: false },
        },
      };
    }, [mainProductDetails]);
    useEffect(() => {
      setFetchFlags({
        products: !!mainProductDetails && otherProducts.length === 0,
        sellers: otherProducts.length > 0,
      });
    }, [mainProductDetails, otherProducts.length]);



    const {
      loadPage,
      productsForDisplay,
      products: productInfos,
    } = useCustomPaginatedProducts({
      limit: 21,
      skip: !fetchFlags.products,
      infoVariablesP: variables,
      setLocalLoading: setProductsLoading,
    });

    useEffect(() => {
      if (!isMainProductloading && otherProducts.length === 0 && !!mainProductDetails) {
        loadPage();
      }

      //eslint-disable-next-line
    }, [mainProductDetails]);

    const { combineProducts, wholeProducts } = useCombineProducts({
      productsForDisplay,
      productInfos,
      brands,
      categories,
    });

    const memoizedCombineProducts = useCallback(() => {
      combineProducts();
      //eslint-disable-next-line
    }, [combineProducts, productInfos, productsForDisplay, brands, categories]);

    useEffect(() => {
      if (!mainProductDetails) return;
      if (otherProducts.length > 0) return;
      if (productsLoading) return;
      memoizedCombineProducts();
      setFetchFlags((prev) => ({
        ...prev,
        products: false,
      }));

      //eslint-disable-next-line
    }, [productsLoading]);

    useEffect(() => {
      setOtherProducts(wholeProducts);
      setOtherSellers(wholeProducts);
    }, [wholeProducts]);

    const fetchSellerDetails = async (sellerId: string) => {
      const sellerInfos = await executeGraphQLMutation({
        query: listUserInfos,
        variables: {
          filter: {
            user_id: { eq: sellerId || "" },
          },
        },
      });
      return sellerInfos?.items?.[0] || {};
    };

    useEffect(() => {
      if (fetchFlags.products) {
        loadPage();
      }
      //eslint-disable-next-line
    }, [fetchFlags.products]);

    useEffect(() => {
      if (fetchFlags.sellers) {
        const fetchAllSellerNames = async () => {
          const ids = otherProducts.map((p) => p?.seller_id).filter(Boolean);
          const uniqueIds = Array.from(new Set(ids));
          const results = await Promise.all(
            uniqueIds.map(async (id) => {
              const seller = await fetchSellerDetails(id);
              return { id, name: seller?.first_name || seller?.name || "Unknown" };
            })
          );
          const names: { [key: string]: string } = {};
          results.forEach(({ id, name }) => {
            names[id] = name;
          });
          setSellerNames(names);
        };
        fetchAllSellerNames();
      }
    }, [fetchFlags.sellers, otherProducts]);

    const increaseQuantity = () => {
      setQuantity((prev) => prev + 1);
    };

    const decreaseQuantity = () => {
      setQuantity((prev) => Math.max(prev - 1, 1));
    };

    const handleAddToCart = () => {
      if (!mainProductDetails) return;
      const productToAdd = { ...mainProductDetails, quantity };
      dispatch(addToCart(productToAdd));
    };

    //   console.log(mainProductDetails, "mainProductDetails");
    return (
      <>
        <SheetContent
          className={cn(
            "sm:max-w-lg w-full p-6 shadow-lg bg-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right data-[state=closed]:duration-200 data-[state=open]:duration-300 inset-y-0 right-0 h-full",
            className
          )}
        >

          <ScrollArea className="h-full">
          {/* <SheetHeader>
          <SheetTitle>
            {mainProductDetails?.name || "Other Seller"}
          </SheetTitle>
          <SheetDescription>
            {mainProductDetails?.brand?.name
              ? `${mainProductDetails.brand.name}${mainProductDetails?.category?.[0]?.name ? " · " + mainProductDetails.category[0].name : ""}`
              : ""}
          </SheetDescription>
        </SheetHeader> */}

          {/* Main product details */}
          <div className="flex gap-4 items-center border-b pb-4 mb-4">
            <img
              src={currentVariant?.images?.[0]}
              alt={mainProductDetails?.name}
              className="w-28 h-28 object-cover rounded border"
            />
            <SheetHeader className="flex-1">
              <SheetTitle className="font-semibold text-base">
                {mainProductDetails?.name}
              </SheetTitle>
              <SheetDescription className="text-lg font-bold text-black mb-1">
                ${currentVariant?.price}
              </SheetDescription>
              <div className="text-xs text-gray-500">
                Seller:{" "}
                <span className="font-medium text-black">
                  {mainProductDetails?.sellerName || "Palette"}
                </span>
              </div>
            </SheetHeader>
            <Button
              size="sm"
              className="bg-black text-white hover:bg-gray-800"
              onClick={() => {
                dispatch(
                  addToCart({ ...currentVariant, name: mainProductDetails?.name, quantity: 1 })
                );
                setSheetOpen(false);
              }}
            >
              Add to Cart
            </Button>
          </div>

          {/* Other sellers list */}
          {productsLoading ? (
            <div className="flex items-center justify-center h-[60vh]">
              <LoaderCircle className="w-6 h-6 animate-spin mr-2" />
              Fetching other options...
            </div>
          ) : (
            <div>
              <div className="font-semibold text-sm mb-2">Other Sellers</div>
              {!otherProducts || otherProducts.length === 0 ? (
                <div className="text-gray-500 text-center py-8">No options available</div>
              ) : (
                <div className="space-y-3">
                  {(otherProducts ?? []).map((product) => (
                    // <div
                    //   key={product?.id}
                    //   className="flex items-center justify-between border rounded p-2"
                    // >
                    //   <Link
                    //     to={`/product/${product?.product_id}`}
                    //     className="flex items-center gap-2"
                    //     onClick={() => setSheetOpen(false)}
                    //   >
                    //     <div>
                    //       <div className="text-base font-bold text-black">${product.price}</div>
                    //       <div className="text-xs text-gray-500">
                    //         Sold by:{" "}
                    //         <span className="font-medium text-black">
                    //           {sellerNames[product?.seller_id] || "..."}
                    //         </span>
                    //       </div>
                    //     </div>
                    //   </Link>
                    //   <Button
                    //     size="sm"
                    //     className="bg-black text-white hover:bg-gray-800"
                    //     onClick={() => {
                    //       dispatch(
                    //         addToCart({ ...product, name: mainProductDetails?.name, quantity: 1 })
                    //       );
                    //       setSheetOpen(false);
                    //     }}
                    //   >
                    //     Add to Cart  
                    //   </Button>
                    // </div>

                    <div className="flex gap-4 items-center border-b pb-4 mb-4" key={product?.id}>
                    <img
                      src={product?.images?.[0]}
                      alt={product?.name}
                      className="w-28 h-28 object-cover rounded border"
                    />
                    <SheetHeader className="flex-1">
                      <SheetTitle className="font-semibold text-base">
                        {product?.name}
                      </SheetTitle>
                      <SheetDescription className="text-lg font-bold text-black mb-1">
                        ${product?.price}
                      </SheetDescription>
                      <div className="text-xs text-gray-500">
                        Seller:{" "}
                        <span className="font-medium text-black">
                        {sellerNames[product?.seller_id] || "..."}
                        </span>
                      </div>
                    </SheetHeader>
                    <Button
                      size="sm"
                      className="bg-black text-white hover:bg-gray-800"
                      onClick={() => {
                        dispatch(
                          addToCart({ ...currentVariant, name: mainProductDetails?.name, quantity: 1 })
                        );
                        setSheetOpen(false);
                      }}
                    >
                      Add to Cart
                    </Button>
                  </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Quantity</span>
              <div className="flex items-center border  rounded-md">
                <Button
                  disabled={quantity === 1}
                  className="px-2 py-1 rounded-l-md rounded-r-none hover:bg-black/80 hover:text-white bg-transparent text-black"
                  onClick={decreaseQuantity}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-10 text-center">{quantity}</span>
                <Button
                  className="px-2 py-1 rounded-r-md rounded-l-none  hover:bg-black/80 hover:text-white bg-transparent text-black"
                  onClick={increaseQuantity}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Button
              onClick={handleAddToCart}
              className="whitespace-nowrap bg-black hover:bg-black/80 text-white w-full"
            >
              Add Selected to Cart<span className="w-5">({quantity})</span>
            </Button>
            <SheetClose asChild>
              <Button className="whitespace-nowrap bg-white hover:bg-black/80 hover:text-white w-full border border-gray-400/50 text-black">
                <X className="h-4 w-4" /> Close
              </Button>
            </SheetClose>
          </div> */}
          </ScrollArea>
        </SheetContent>
      </>
    );
  }
);

export default OtherSellersSheet;
